interface TomlGeneratorParams {
  config: Record<string, any>
  studyName?: string
  symbol?: string
  timeframe?: string
  features?: Array<{ name: string; period: number; period2?: number }>
  pinnedBooleans?: Record<string, boolean>
  samplerSeed?: number | null
}

export function generateTomlConfig({
  config,
  studyName = "lorentzian_study",
  symbol = "BTC_USD",
  timeframe = "D1",
  features = [
    { name: "RSI", period: 14 },
    { name: "WT", period: 10, period2: 11 },
    { name: "CCI", period: 20 },
    { name: "ADX", period: 20, period2: 2 },
    { name: "RSI", period: 9 }
  ],
  pinnedBooleans = {},
  samplerSeed = null
}: TomlGeneratorParams): string {
  // Start with header comment
  let toml = `# ${studyName} - v4.3 Sigmoid Confidence Scaling
# Version: 4.3 - Sigmoid trade confidence with guardrails
# Target trades: 100 (50% confidence point)
# Sigmoid steepness: 2.0 (confidence curve sharpness)
# Objective: Sortino * sigmoid(k*(log(trades) - log(target)))
# Generated at: {{TIMESTAMP}}

`

  // Add features first (exactly as in the template)
  features.forEach((feature) => {
    toml += `[[features]]
name = "${feature.name}"
period = ${feature.period}
`
    if (feature.period2) {
      toml += `period2 = ${feature.period2}
`
    }
    toml += '\n'
  })

  // Add study configuration
  toml += `[study]
name = "${studyName}"
direction = "maximize"
n_trials = ${config.n_trials || 300}
n_jobs = 4
storage = "postgresql://jdehorty@localhost:5432/optuna_lorentzian"
load_if_exists = true

[data]
path = "data/${symbol}_${timeframe}.json"

[fixed_parameters]
usekernelsmoothing = true
min_trades_required = 10
target_trades = 100
sigmoid_steepness = 2.0
${Object.entries(pinnedBooleans).map(([key, value]) => `${key} = ${value}`).join('\n')}

[study.sampler]
type = "TPE"${samplerSeed !== null ? `\nseed = ${samplerSeed}` : ''}
n_startup_trials = 40
n_ei_candidates = 32

[study.pruner]
type = "MedianPruner"
n_startup_trials = 10
n_warmup_steps = 0
interval_steps = 1
n_min_trials = 5

`

  // Add parameters exactly as in the template
  // neighborscount
  if (config.neighborscount_range || typeof config.neighborscount === 'number') {
    toml += `[parameters.neighborscount]
type = "int"
`
    if (Array.isArray(config.neighborscount_range)) {
      toml += `low = ${config.neighborscount_range[0]}
high = ${config.neighborscount_range[1]}
`
    } else if (typeof config.neighborscount === 'number') {
      toml += `low = ${config.neighborscount}
high = ${config.neighborscount}
`
    } else {
      toml += `low = 5
high = 100
`
    }
    toml += `description = "KNN neighbors for classification (expanded range)"

`
  }

  // maxbarsback
  if (config.maxbarsback_range || typeof config.maxbarsback === 'number') {
    toml += `[parameters.maxbarsback]
type = "int"
`
    if (Array.isArray(config.maxbarsback_range)) {
      toml += `low = ${config.maxbarsback_range[0]}
high = ${config.maxbarsback_range[1]}
`
    } else if (typeof config.maxbarsback === 'number') {
      toml += `low = ${config.maxbarsback}
high = ${config.maxbarsback}
`
    } else {
      toml += `low = 500
high = 3900
`
    }
    toml += `step = 50
description = "${symbol}: Extended history for robust Sortino calculations"

`
  }

  // emaperiod - skip if EMA filter is OFF FIXED
  if (pinnedBooleans['useemafilter'] !== false) {
    toml += `[parameters.emaperiod]
type = "int"
low = ${Array.isArray(config.emaperiod) ? config.emaperiod[0] : 10}
high = ${Array.isArray(config.emaperiod) ? config.emaperiod[1] : 600}
step = 10

`
  }

  // smaperiod - skip if SMA filter is OFF FIXED
  if (pinnedBooleans['usesmafilter'] !== false) {
    toml += `[parameters.smaperiod]
type = "int"
low = ${Array.isArray(config.smaperiod) ? config.smaperiod[0] : 20}
high = ${Array.isArray(config.smaperiod) ? config.smaperiod[1] : 600}
step = 10

`
  }

  // lookbackwindow - skip if kernel smoothing is OFF FIXED
  if (pinnedBooleans['usekernelsmoothing'] !== false) {
    toml += `[parameters.lookbackwindow]
type = "int"
low = ${Array.isArray(config.lookbackwindow) ? config.lookbackwindow[0] : 3}
high = ${Array.isArray(config.lookbackwindow) ? config.lookbackwindow[1] : 20}

`
  }

  // relativeweight - skip if kernel smoothing is OFF FIXED
  if (pinnedBooleans['usekernelsmoothing'] !== false) {
    toml += `[parameters.relativeweight]
type = "float"
low = ${Array.isArray(config.relativeweight) ? config.relativeweight[0] : 1.0}
high = ${Array.isArray(config.relativeweight) ? config.relativeweight[1] : 50.0}

`
  }

  // regressionlevel - skip if kernel smoothing is OFF FIXED
  if (pinnedBooleans['usekernelsmoothing'] !== false) {
    toml += `[parameters.regressionlevel]
type = "int"
low = ${Array.isArray(config.regressionlevel) ? config.regressionlevel[0] : 5}
high = ${Array.isArray(config.regressionlevel) ? config.regressionlevel[1] : 75}

`
  }

  // crossoverlag - skip if kernel smoothing is OFF FIXED
  if (pinnedBooleans['usekernelsmoothing'] !== false) {
    toml += `[parameters.crossoverlag]
type = "int"
low = ${Array.isArray(config.crossoverlag) ? config.crossoverlag[0] : 2}
high = ${Array.isArray(config.crossoverlag) ? config.crossoverlag[1] : 8}

`
  }

  // regimethreshold - skip if Regime filter is OFF FIXED
  if (pinnedBooleans['useregimefilter'] !== false) {
    toml += `[parameters.regimethreshold]
type = "float"
low = ${Array.isArray(config.regimethreshold) ? config.regimethreshold[0] : -2.5}
high = ${Array.isArray(config.regimethreshold) ? config.regimethreshold[1] : 2.5}
step = 0.05
description = "${symbol}: Tighter regime for volatility control"

`
  }

  // adxthreshold - skip if ADX filter is OFF FIXED
  if (pinnedBooleans['useadxfilter'] !== false) {
    toml += `[parameters.adxthreshold]
type = "int"
low = ${Array.isArray(config.adxthreshold) ? config.adxthreshold[0] : 20}
high = ${Array.isArray(config.adxthreshold) ? config.adxthreshold[1] : 40}

`
  }

  // Boolean parameters - only generate if not pinned
  const booleanParams = [
    'usevolatilityfilter',
    'useregimefilter',
    'useadxfilter',
    'usesmafilter',
    'useemafilter',
    'usedynamicexits'
  ];

  booleanParams.forEach((param) => {
    // Skip if pinned - pinned booleans are omitted from TOML (backend will handle defaults)
    if (pinnedBooleans[param] !== undefined) {
      return;
    }

    // Generate categorical parameter for non-pinned booleans
    toml += `[parameters.${param}]
type = "categorical"
choices = [ true, false ]

`;
  });

  
  return toml
}